// @mui
import {
  AppBar,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Popover,
  ListItem,
  List,
  Chip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// utils
import { DatePicker } from '@mui/x-date-pickers-pro';
import { fromUnixTime, getUnixTime } from 'date-fns';
import { bgBlur } from '../../../utils/cssStyles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// config
import { HEADER, NAV } from '../../../config-global';
// components
import Logo from '../../../components/logo';
import Iconify from '../../../components/iconify';
import { useSettingsContext } from '../../../components/settings';
//
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';
import { useSelector } from '../../../redux/store';
import RefreshIconButton from '../../../components/refresh-icon-button/RefreshIconButton';
import { executeRefreshAction } from '../../../redux/slices/refreshSlice';
import {
  getApplicationTimestamp,
  setApplicationTimestamp,
} from '../../../utils/applicationTimestamp';
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
import FaqItem from './FaqItem';
import { useEffect, useState } from 'react';
import { useLocales } from '../../../locales';
import {
  selectAllTransportationPeriods,
  useGetTransportationPeriodQuery,
} from '../../../redux/slices/transportationPeriodSlice';
import { fDateDe, getDayTimestamp, getUnixTimeEndOfDay } from '../../../utils/timeAndDateHelper';
import {
  getApplicationTransportationPeriodId,
  setApplicationTransportationPeriodId,
} from '../../../utils/applicationTransportationPeriod';
import { Box } from '@mui/system';
import { useGetConfigurationQuery } from '../../../redux/slices/apiSlice';
import ReleaseNotesDrawer from './ReleaseNotesDrawer';
import type { TransportationPeriod } from '../../../@types/configuration';

// ----------------------------------------------------------------------

const normalizeHexColor = (color?: string) => {
  const normalizedColor = color?.trim();

  return normalizedColor && /^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(normalizedColor)
    ? normalizedColor
    : undefined;
};

const isTimestampWithinTransportationPeriod = (
  timestamp: number,
  transportationPeriod?: TransportationPeriod
) => {
  if (!transportationPeriod) {
    return true;
  }

  const dayTimestamp = getDayTimestamp(fromUnixTime(timestamp));
  const validFromTimestamp = getDayTimestamp(transportationPeriod.validFrom);
  const validUntilTimestamp = getDayTimestamp(transportationPeriod.validUntil);

  return (
    dayTimestamp !== null &&
    (validFromTimestamp === null || dayTimestamp >= validFromTimestamp) &&
    (validUntilTimestamp === null || dayTimestamp <= validUntilTimestamp)
  );
};

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const { translate } = useLocales();
  const { data: configuration } = useGetConfigurationQuery();
  const headerColor = normalizeHexColor(configuration?.headerColor);
  const headerTitle = configuration?.headerTitle?.trim();
  const headerContrastColor = headerColor ? theme.palette.getContrastText(headerColor) : undefined;

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;
  const currentDate = getUnixTime(new Date());
  const [timestamp, setTimestamp] = useState<number>(() => getApplicationTimestamp());

  const isSameDate = (date: number) => {
    return fromUnixTime(currentDate).toDateString() === fromUnixTime(date).toDateString();
  };
  const [isMatchingDate, setIsMatchingDate] = useState<boolean>(() => isSameDate(timestamp));
  const { hasRefreshAction, isLoading, tooltipTitle } = useSelector((state) => state.refresh);
  // A view is currently shown without date consideration, so the global date has no effect on it
  const isGlobalDateIgnored = useSelector((state) => state.globalDate.isIgnoredByCurrentView);

  const [transportationPeriodAnchorElement, setTransportationPeriodAnchorElement] =
    useState<HTMLElement | null>(null);
  const [selectedTransportationPeriodId, setSelectedTransportationPeriodId] = useState<string>(
    () => {
      const id = getApplicationTransportationPeriodId();
      return id !== '0' ? id : '';
    }
  );

  useGetTransportationPeriodQuery();
  const transportationPeriods = useSelector(selectAllTransportationPeriods);
  const selectedTransportationPeriod = transportationPeriods.find(
    (transportationPeriod) => String(transportationPeriod.id) === selectedTransportationPeriodId
  );

  const updateTimestamp = (newTimestamp: number, transportationPeriod?: TransportationPeriod) => {
    const effectiveTransportationPeriod = transportationPeriod ?? selectedTransportationPeriod;
    if (!isTimestampWithinTransportationPeriod(newTimestamp, effectiveTransportationPeriod)) {
      return;
    }

    setApplicationTimestamp(newTimestamp);
    setTimestamp(newTimestamp);
    setIsMatchingDate(isSameDate(newTimestamp));
  };

  const updateGlobalTransportationPeriod = (
    transportationPeriodId: string,
    transportationPeriod?: TransportationPeriod
  ) => {
    const normalizedTransportationPeriodId = String(transportationPeriodId);
    const transportationPeriodChanged =
      normalizedTransportationPeriodId !== selectedTransportationPeriodId;
    setSelectedTransportationPeriodId(normalizedTransportationPeriodId);
    setApplicationTransportationPeriodId(
      normalizedTransportationPeriodId !== '' ? normalizedTransportationPeriodId : '0'
    );

    if (transportationPeriodChanged && transportationPeriod?.validFrom) {
      updateTimestamp(getUnixTimeEndOfDay(transportationPeriod.validFrom), transportationPeriod);
    }
  };

  useEffect(() => {
    if (
      !selectedTransportationPeriod?.validFrom ||
      isTimestampWithinTransportationPeriod(timestamp, selectedTransportationPeriod)
    ) {
      return;
    }

    const periodStartTimestamp = getUnixTimeEndOfDay(selectedTransportationPeriod.validFrom);
    setApplicationTimestamp(periodStartTimestamp);
    setTimestamp(periodStartTimestamp);
    setIsMatchingDate(
      new Date().toDateString() === fromUnixTime(periodStartTimestamp).toDateString()
    );
  }, [selectedTransportationPeriod, timestamp]);

  const selectedTransportationPeriodName = selectedTransportationPeriod?.name;

  const handleTransportationPeriodMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTransportationPeriodAnchorElement(event.currentTarget);
  };

  const handleTransportationPeriodMenuClose = () => {
    setTransportationPeriodAnchorElement(null);
  };

  const transportationPeriodPopoverOpen = Boolean(transportationPeriodAnchorElement);

  const renderTransportationPeriodDate = (
    date: Date | null | undefined,
    dateTooltipTitle: string,
    isSelected: boolean,
    onClickDate: () => void
  ) => {
    if (!date) return null;
    return (
      <Tooltip title={isSelected ? dateTooltipTitle : ''} disableHoverListener={!isSelected}>
        <Typography
          variant="caption"
          onClick={isSelected ? onClickDate : undefined}
          sx={{
            cursor: isSelected ? 'pointer' : 'default',
            fontSize: '0.85rem',
            color: isSelected ? 'primary.main' : 'text.secondary',
            '&:hover': isSelected ? { textDecoration: 'underline' } : {},
          }}
        >
          {fDateDe(date)}
        </Typography>
      </Tooltip>
    );
  };

  const renderTransportationPeriodItem = (
    id: string,
    label: string,
    onClick: () => void,
    chips?: React.ReactNode
  ) => {
    const isSelected = selectedTransportationPeriodId === id;
    return (
      <ListItem
        key={id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          py: 0.5,
          px: 1.5,
          bgcolor: isSelected ? 'action.selected' : 'transparent',
        }}
      >
        <Chip
          label={label}
          size="small"
          variant={isSelected ? 'filled' : 'outlined'}
          onClick={onClick}
          sx={{
            flexGrow: 1,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: isSelected ? 700 : 400,
            color: isSelected ? 'primary.main' : 'text.primary',
            borderColor: isSelected ? 'primary.main' : 'divider',
            width: 160,
            minWidth: 0,
            justifyContent: 'center',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
        {chips && (
          <Stack direction="row" spacing={1.5} flexShrink={0}>
            {chips}
          </Stack>
        )}
      </ListItem>
    );
  };

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}
      {!isDesktop && (
        <IconButton
          onClick={onOpenNav}
          sx={{ mr: 1, color: headerContrastColor ?? 'text.primary' }}
        >
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {headerTitle && (
        <Typography
          variant="subtitle1"
          noWrap
          title={headerTitle}
          sx={{
            display: { xs: 'none', md: 'block' },
            maxWidth: { md: 240, xl: 400 },
            mr: 2,
            color: headerContrastColor ?? 'text.primary',
          }}
        >
          {headerTitle}
        </Typography>
      )}

      {/* FIXME This is uncommented for now */}
      {/* <Searchbar /> */}
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
        // Aligned to the top instead of the middle, so that the transportation period chip rendered
        // below the date input still fits into the header
        sx={{ alignSelf: 'flex-start', mt: '3px' }}
      >
        {/*Refresh button is always first place!*/}
        <RefreshIconButton
          tooltipTitle={hasRefreshAction ? tooltipTitle : `${translate('refresh.unavailable')}`}
          loading={isLoading}
          onClick={hasRefreshAction ? executeRefreshAction : undefined}
          disabled={!hasRefreshAction || isLoading}
          size="large"
        />

        <RoleBasedGuard
          roles={[
            'traffic-read',
            'school-read',
            'transport-read',
            'school-absence',
            'tour-read',
            'export-read',
          ]}
        >
          <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }}>
            {!isMatchingDate &&
              isTimestampWithinTransportationPeriod(currentDate, selectedTransportationPeriod) && (
                <Tooltip title={`${translate('dateAlert')}`}>
                  <IconButton
                    onClick={() => {
                      updateTimestamp(currentDate);
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      color: headerContrastColor ?? theme.palette.warning.light,
                      ...(headerContrastColor && {
                        border: `1px solid ${alpha(headerContrastColor, 0.32)}`,
                        backgroundColor: alpha(headerContrastColor, 0.12),
                        '&:hover': {
                          backgroundColor: alpha(headerContrastColor, 0.2),
                        },
                      }),
                    }}
                  >
                    <Iconify icon="eva:alert-circle-fill" color="inherit" />
                  </IconButton>
                </Tooltip>
              )}

            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Tooltip
                title={isGlobalDateIgnored ? `${translate('globalDateIgnoredTooltip')}` : ''}
                disableHoverListener={!isGlobalDateIgnored}
              >
                {/* span keeps the tooltip working while the picker is disabled */}
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <DatePicker
                    value={fromUnixTime(timestamp)}
                    format="EEEE dd.MM.yyyy"
                    disabled={isGlobalDateIgnored}
                    minDate={
                      selectedTransportationPeriod?.validFrom
                        ? new Date(selectedTransportationPeriod.validFrom)
                        : undefined
                    }
                    maxDate={
                      selectedTransportationPeriod?.validUntil
                        ? new Date(selectedTransportationPeriod.validUntil)
                        : undefined
                    }
                    onChange={(value) => {
                      updateTimestamp(getUnixTimeEndOfDay(value!));
                    }}
                    slotProps={{
                      textField: {
                        sx: isGlobalDateIgnored
                          ? {
                              '& .MuiInputBase-input.Mui-disabled': {
                                textDecoration: 'line-through',
                              },
                            }
                          : undefined,
                      },
                    }}
                  />
                </Box>
              </Tooltip>

              {/* Positioned below the input so that only the input itself determines the
                  vertical alignment of the header row */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Tooltip title={`${translate('transportationPeriod.select')}`}>
                  <Chip
                    label={
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 0,
                          maxWidth: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {selectedTransportationPeriodName
                            ? `${translate('transportationPeriod.singular')} ${selectedTransportationPeriodName}`
                            : `${translate('transportationPeriod.noneSelected')}`}
                        </Box>
                        <Iconify icon="eva:more-vertical-fill" width={16} height={16} />
                      </Box>
                    }
                    size="small"
                    onClick={handleTransportationPeriodMenuOpen}
                    sx={{
                      mt: 0.5,
                      height: 26,
                      borderRadius: 1,
                      maxWidth: { xs: 240, sm: 280, md: 320 },
                      minWidth: 0,
                      color: selectedTransportationPeriodName ? 'primary.main' : 'text.secondary',
                      bgcolor: theme.palette.mode === 'light' ? 'grey.200' : 'grey.700',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'light' ? 'grey.300' : 'grey.600',
                      },
                      '& .MuiChip-label': {
                        px: 1.25,
                        minWidth: 0,
                        maxWidth: 1,
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Stack>

          <Popover
            open={transportationPeriodPopoverOpen}
            anchorEl={transportationPeriodAnchorElement}
            onClose={handleTransportationPeriodMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <List disablePadding sx={{ minWidth: 280, py: 0.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ px: 1.5, py: 0.75 }}
              >
                <Typography variant="subtitle2" sx={{ py: 0.75, fontWeight: 700 }}>
                  {`${translate('transportationPeriod.select')}`}
                </Typography>
                <Tooltip title={`${translate('transportationPeriod.helpInfoText')}`}>
                  <IconButton size="small" sx={{ color: 'text.secondary' }}>
                    <Iconify icon="mdi:information-outline" width={20} height={20} />
                  </IconButton>
                </Tooltip>
              </Stack>
              {renderTransportationPeriodItem(
                '',
                `${translate('transportationPeriod.noneSelected')}`,
                () => {
                  updateGlobalTransportationPeriod('');
                  handleTransportationPeriodMenuClose();
                }
              )}
              {transportationPeriods.map((transportationPeriod) => {
                const transportationPeriodId = String(transportationPeriod.id);
                const isSelected = selectedTransportationPeriodId === transportationPeriodId;

                return renderTransportationPeriodItem(
                  transportationPeriodId,
                  transportationPeriod.name,
                  () =>
                    updateGlobalTransportationPeriod(transportationPeriodId, transportationPeriod),
                  <>
                    {renderTransportationPeriodDate(
                      transportationPeriod.validFrom,
                      `${translate('transportationPeriod.selectValidFrom')}`,
                      isSelected,
                      () => {
                        updateTimestamp(
                          getUnixTimeEndOfDay(transportationPeriod.validFrom!),
                          transportationPeriod
                        );
                        handleTransportationPeriodMenuClose();
                      }
                    )}
                    {renderTransportationPeriodDate(
                      transportationPeriod.validUntil,
                      `${translate('transportationPeriod.selectValidUntil')}`,
                      isSelected,
                      () => {
                        updateTimestamp(
                          getUnixTimeEndOfDay(transportationPeriod.validUntil!),
                          transportationPeriod
                        );
                        handleTransportationPeriodMenuClose();
                      }
                    )}
                  </>
                );
              })}
            </List>
          </Popover>
        </RoleBasedGuard>

        <AccountPopover />

        <FaqItem />

        <ReleaseNotesDrawer />

        <RoleBasedGuard
          roles={[
            'traffic-read',
            'school-read',
            'transport-read',
            'school-absence',
            'tour-read',
            'export-read',
            'employeeAppDispo-read',
            'superuser,',
          ]}
        >
          <NotificationsPopover />
        </RoleBasedGuard>
      </Stack>
    </>
  );
  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
        ...(headerColor && {
          color: headerContrastColor,
          backgroundColor: alpha(headerColor, 0.5),
          '& .MuiIconButton-root': {
            color: 'inherit',
          },
          '& .MuiInputBase-root': {
            color: 'inherit',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(headerContrastColor as string, 0.9),
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(headerContrastColor as string, 0.9),
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: headerContrastColor,
          },
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
